import { useEffect, useState, useRef } from 'react';
import 'firebaseui/dist/firebaseui.css';
import { auth, firebase } from '../shared/firebase-config';
import * as firebaseui from 'firebaseui';
import { Modal } from '../components/ui';

export default function Login() {
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const uiRef = useRef<firebaseui.auth.AuthUI | null>(null);
  const isInitializingRef = useRef(false);
  const isMountedRef = useRef(true);
  const createdInstanceRef = useRef(false);

  useEffect(() => {
    // Auto-redirect if already signed in
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        // Check admin claim
        const token = await user.getIdToken(true);
        const decodedToken = await user.getIdTokenResult();
        if (decodedToken.claims.admin === true) {
          window.location.href = '/';
        }
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    if (isInitializingRef.current) return;
    if (uiRef.current) return;

    isInitializingRef.current = true;
    isMountedRef.current = true;

    let ui: firebaseui.auth.AuthUI;
    let isReusingInstance = false;

    // Try to get existing instance or create new one
    try {
      const existingUI = firebaseui.auth.AuthUI.getInstance();
      if (existingUI) {
        // Reuse existing instance - don't delete it in cleanup since it's a singleton
        ui = existingUI;
        isReusingInstance = true;
        createdInstanceRef.current = false;
      } else {
        // No existing instance, create new one
        ui = new firebaseui.auth.AuthUI(auth);
        createdInstanceRef.current = true;
      }
    } catch (error) {
      // If getInstance throws, try to create new instance
      // This might fail if instance exists, but we'll handle that
      try {
        ui = new firebaseui.auth.AuthUI(auth);
        createdInstanceRef.current = true;
      } catch (createError: any) {
        // If creation fails because instance exists, try to get it
        if (createError.message?.includes('already exists')) {
          const existingUI = firebaseui.auth.AuthUI.getInstance();
          if (existingUI) {
            ui = existingUI;
            isReusingInstance = true;
            createdInstanceRef.current = false;
          } else {
            isInitializingRef.current = false;
            throw createError;
          }
        } else {
          isInitializingRef.current = false;
          throw createError;
        }
      }
    }

    uiRef.current = ui;

    const uiConfig: firebaseui.auth.Config = {
      signInFlow: 'popup',
      signInOptions: [
        {
          provider: firebase.auth.EmailAuthProvider.PROVIDER_ID,
          requireDisplayName: false
        }
      ],
      signInSuccessUrl: '/', // Set to prevent warning; callback handles actual redirect
      credentialHelper: firebaseui.auth.CredentialHelper.NONE,
      callbacks: {
        signInSuccessWithAuthResult: async (authResult) => {
          const user = authResult.user;
          console.log('Sign in successful:', user.uid, user.email);

          try {
            // Check admin claim
            const token = await user.getIdToken(true);
            const decodedToken = await user.getIdTokenResult();
            
            if (decodedToken.claims.admin === true) {
              // User has admin claim, redirect to admin dashboard
              window.location.href = '/';
              return false; // Prevent FirebaseUI redirect
            } else {
              // User doesn't have admin claim, sign them out and show error
              await auth.signOut();
              setErrorMessage('Access denied. This account does not have admin privileges.');
              setErrorModalOpen(true);
              return false; // Prevent FirebaseUI redirect
            }
          } catch (error) {
            console.error('Error checking admin claim:', error);
            await auth.signOut();
            setErrorMessage('Failed to verify admin access. Please try again.');
            setErrorModalOpen(true);
            return false; // Prevent FirebaseUI redirect
          }
        },
        uiShown: () => {
          console.log('FirebaseUI widget rendered');
        },
        signInFailure: (error) => {
          console.error('Sign in failure:', error);
          setErrorMessage('Failed to sign in. Please check your credentials and try again.');
          setErrorModalOpen(true);
          return Promise.resolve();
        }
      },
      // Hide terms of service and privacy policy
      tosUrl: undefined,
      privacyPolicyUrl: undefined
    };

    // Reset if reusing existing instance, then start
    if (isReusingInstance) {
      ui.reset();
    }
    
    // Start FirebaseUI
    ui.start('#firebaseui-auth-container', uiConfig);
    isInitializingRef.current = false;

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      // Don't delete singleton instances - FirebaseUI manages them
      // Only clear our reference
      // In React StrictMode, the instance will be reused on remount
      uiRef.current = null;
      isInitializingRef.current = false;
    };
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-border/20">
      <div className="w-full max-w-md rounded-2xl border border-neutral-border bg-white p-8 shadow-card">
        <h1 className="mb-2 text-2xl font-bold text-neutral-primary">Gantty Admin</h1>
        <p className="mb-6 text-center text-sm text-neutral-secondary">
          Sign in with your admin account to continue
        </p>
        <div id="firebaseui-auth-container" ref={containerRef}></div>
      </div>
      <Modal
        open={errorModalOpen}
        onClose={() => { setErrorModalOpen(false); setErrorMessage(''); }}
        message={errorMessage}
        variant="alert"
      />
    </div>
  );
}
